import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import QueryString from 'qs';
import moment from 'moment';

import { Backdrop, Box, CircularProgress } from '@mui/material';
import AnimationPage from '../../../pages/AnimationPage';
import GroupLessonEditModal from '../lessonModal/GroupLessonEditModal/GroupLessonEditModal';
import LessonUsers from '../LessonUsers';
import GroupLessonHeader from './GroupLessonHeader';
import GroupLessonCalendar from './GroupLessonCalendar';
import GroupLessonGuideModal from './GroupLessonGuideModal';
import {
  CancelSuccessModal,
  BusyDatesModal,
  CancelTypeDialog,
  EventMenu,
  CopyWeeklyConfirmModal,
} from './GroupLessonModals';
import TemplateManageModal from './groupLessonTemplate/TemplateManageModal';
import ScheduleCreateModal from './groupLessonTemplate/ScheduleCreateModal';
import SchedulePasteLayer from '../scheduleClipboard/SchedulePasteLayer';
import { useSchedulePaste } from '../scheduleClipboard/useSchedulePaste';
import { useScheduleDelete } from '../scheduleClipboard/useScheduleDelete';
import GroupLessonBulkEditModal from './GroupLessonBulkEditModal';
import GroupBatchReserveModal from './GroupBatchReserveModal';
import GroupScheduleSettingModal from './GroupScheduleSettingModal';
import { TrainerFilterModal } from '../privateLesson/PrivateLessonModals';
import { useGroupScheduleSetting } from './groupScheduleSetting';

import { getBranchTime } from '../../../api/center-branch';
import { deleteWeekByIdAndType, getAllLessonsByType, getLessonById, createLesson } from '../../../api/lesson';
import { withOverlapConfirm } from '../../../utils/overlapConfirm';
import { getAllPointRooms } from '../../../api/point';
import { getPointTrainers } from '../../../api/staff';
import { getTicketsByType } from '../../../api/ticket';
import { errorMessages, getAllLessonEvents, toTop } from '../../../utils/methods';
import { scrollToCard } from '../../../utils/scrollToCard';
import { createEventId } from '../event-utils';
import { formatWeekTitle, startOfWeekBy } from '../weekStart';
import { filterRoomsByLessonType } from '../../../constants/roomTypes';

import { PageContainer, ContentWrapper } from './GroupLessonStyled';
import useUserAuth from '../../../utils/useUserAuth';

const DAY_END_MINUTES = 24 * 60;
// 시간 선택지는 5분 단위 23:55 까지
const LAST_SELECTABLE_MINUTES = 23 * 60 + 55;
// 빈칸 클릭 생성의 기본 수업 길이(기존 addMinutesToTime 과 같은 50분)
const DEFAULT_CLASS_MINUTES = 50;

// 임직원·강의실 필터 보관 — 지점 스케줄(PrivateLessonList)의 filterMemo 와 같은 방식, 값은 지점 스케줄과 따로 둔다
let groupFilterMemo = {};
const readGroupFilterMemo = branchId => (groupFilterMemo.branchId === branchId ? groupFilterMemo : null);

const GroupLesson = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);
  const { t } = useTranslation();
  const { mainLang } = useSelector(state => state.lang);

  // 권한 체크
  const canGroupCreate = useUserAuth('SCHEDULE_GROUP_CREATE');
  const canGroupEdit = useUserAuth('SCHEDULE_GROUP_EDIT');
  const canGroupAttendance = useUserAuth('SCHEDULE_GROUP_ATTENDANCE');
  const canGroupCancel = useUserAuth('SCHEDULE_GROUP_CANCEL');
  const canManageRooms = useUserAuth('CENTER_STORE_VIEW');

  // 개인 스케줄 패널 "수업으로 이동" 진입 파라미터 (시안 4a) — 대상 주간으로 이동 후 도착 하이라이트
  const [searchParams, setSearchParams] = useSearchParams();
  const focusClassTimeIdParam = searchParams.get('focusClassTimeId');
  const focusDateParam = searchParams.get('focusDate');
  const focusAppliedRef = useRef(false);
  const [highlightedClassTimeIds, setHighlightedClassTimeIds] = useState([]);
  // 하이라이트 해제 타이머 — 연속으로 강조하면 이전 타이머가 새 강조를 일찍 끄지 않게 하나만 유지한다
  const highlightClearTimerRef = useRef(null);
  const highlightDelayTimerRef = useRef(null);
  const flashHighlight = ids => {
    clearTimeout(highlightClearTimerRef.current);
    setHighlightedClassTimeIds(ids);
    highlightClearTimerRef.current = setTimeout(() => setHighlightedClassTimeIds([]), 5000);
  };
  useEffect(
    () => () => {
      clearTimeout(highlightClearTimerRef.current);
      clearTimeout(highlightDelayTimerRef.current);
    },
    [],
  );
  const prevLessonIdsRef = useRef(new Set());

  // UI 상태
  const [calenderObj, setCalendarObj] = useState();
  const [anchorEl, setAnchorEl] = useState();
  const [active, setActive] = useState();
  const [selectedTabChild, setSelectedTabChild] = useState(1);
  // 보고 있는 주의 기준일 — 주 첫날(currentWeekStart)은 설정의 주 시작 요일로 계산한다(아래 groupSetting 다음).
  // 기준일을 따로 두어 주 시작 요일을 바꿨다 되돌려도 보던 주가 밀리지 않는다
  const [weekAnchor, setWeekAnchor] = useState(() => (focusDateParam ? moment(focusDateParam) : moment()));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cellHeight, setCellHeight] = useState(() => {
    const saved = localStorage.getItem('groupLessonCellHeight');
    return saved ? Number(saved) : 60;
  });
  const handleZoomIn = () =>
    setCellHeight(prev => {
      const next = Math.min(prev + 10, 120);
      localStorage.setItem('groupLessonCellHeight', next);
      return next;
    });
  const handleZoomOut = () =>
    setCellHeight(prev => {
      const next = Math.max(prev - 10, 40);
      localStorage.setItem('groupLessonCellHeight', next);
      return next;
    });
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
  // 임직원·강의실 필터(다중) — 아무것도 안 고르면 전체. 지점 스케줄 임직원 필터처럼 모듈 스코프에 보관해 메뉴를 다녀와도 복원된다(지점과 별개)
  const [selectedTrainerIds, setSelectedTrainerIds] = useState(
    () => readGroupFilterMemo(user?.branch?.branchId)?.selectedTrainerIds ?? [],
  );
  const [selectedRoomIds, setSelectedRoomIds] = useState(
    () => readGroupFilterMemo(user?.branch?.branchId)?.selectedRoomIds ?? [],
  );
  const [openTrainerFilter, setOpenTrainerFilter] = useState(false);
  const [openRoomFilter, setOpenRoomFilter] = useState(false);
  useEffect(() => {
    groupFilterMemo = { branchId: user?.branch?.branchId, selectedTrainerIds, selectedRoomIds };
  }, [user?.branch?.branchId, selectedTrainerIds, selectedRoomIds]);
  const [copyWeeklyModalOpen, setCopyWeeklyModalOpen] = useState(false);
  const [copyOpenYn, setCopyOpenYn] = useState('N');
  const [copySourceFrom, setCopySourceFrom] = useState('');
  const [copySourceTo, setCopySourceTo] = useState('');
  const [copyTargetFrom, setCopyTargetFrom] = useState('');
  const [copyTargetTo, setCopyTargetTo] = useState('');
  const [templateManageOpen, setTemplateManageOpen] = useState(false);
  const [scheduleCreateOpen, setScheduleCreateOpen] = useState(false);
  const [batchReserveOpen, setBatchReserveOpen] = useState(false); // 기존 수업 × 여러 회원 일괄예약
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const [lessonData, setLessonData] = useState({
    name: '',
    periodStart: '',
    periodEnd: '',
    type: 1,
  });

  const [opens, setOpens] = useState({
    open: false,
    update: true,
    open1: false,
    open2: false,
    open3: false,
    open4: false,
    open5: false,
    openMenu: false,
    openMenu1: false,
    openCancelDialog: false,
    trainers: [],
    trainerId: null,
    roomId: null,
    busyDates: [],
  });

  // ============================================
  // React Query - 데이터 조회
  // ============================================

  // 지점 운영 시간 조회
  const { data: branchTime } = useQuery({
    queryKey: ['branchTime', user?.branch?.branchId],
    queryFn: () => getBranchTime(user.branch.branchId, user.token),
    enabled: !!user?.branch?.branchId && !!user?.token,
  });

  // 지점 공용 캘린더 시간 (지점 스케줄 설정에서 바꾼다)
  const sharedPeriodTimes = useMemo(
    () => ({
      startTime: branchTime?.startTime ?? 0,
      endTime: branchTime?.endTime ?? 24,
    }),
    [branchTime],
  );

  // 그룹 스케줄 설정 — 실패·없음이면 기본값(1시간 칸·1시간 선택·카드 최소 48분·공용 캘린더·기본 강의실 없음)
  const {
    setting: savedGroupSetting,
    isLoading: groupSettingLoading,
    isError: groupSettingError,
  } = useGroupScheduleSetting(user?.branch?.branchId, user?.token);
  // 설정 모달에서 바꾼 값의 미리보기 — 저장 전에도 화면은 이 값으로 그린다. 닫으면(저장·취소) 걷는다
  const [groupSettingPreview, setGroupSettingPreview] = useState(null);
  const groupSetting = groupSettingPreview ?? savedGroupSetting;
  const closeGroupSetting = () => {
    setSettingsOpen(false);
    setGroupSettingPreview(null);
  };

  // 보고 있는 주(첫날 ~ 6일 뒤) — 주 시작 요일 설정(미리보기 포함)을 따른다. 조회 기간도 같은 7일
  const currentWeekStart = useMemo(
    () => startOfWeekBy(weekAnchor, groupSetting.weekStartDay),
    [weekAnchor, groupSetting.weekStartDay],
  );
  const dateRange = useMemo(
    () => ({
      start: currentWeekStart.format('YYYY-MM-DD'),
      end: currentWeekStart.clone().add(6, 'days').format('YYYY-MM-DD'),
    }),
    [currentWeekStart],
  );

  // 그룹 전용 캘린더 시간이 있으면 그것, 없으면 공용 시간
  const periodTimes = useMemo(
    () =>
      groupSetting.calendarStartTime != null
        ? { startTime: groupSetting.calendarStartTime, endTime: groupSetting.calendarEndTime }
        : sharedPeriodTimes,
    [groupSetting.calendarStartTime, groupSetting.calendarEndTime, sharedPeriodTimes],
  );

  // 수강권 목록 조회
  const { data: tickets = [] } = useQuery({
    queryKey: ['tickets', user?.branch?.branchId, 'GROUP_LESSON'],
    queryFn: () => {
      const queryString = QueryString.stringify({
        branchId: user?.branch?.branchId,
        courseClassType: 'GROUP_LESSON',
      });
      return getTicketsByType(queryString, user.token);
    },
    enabled: !!user?.branch?.branchId && !!user?.token,
  });

  // 강사 목록 조회
  const { data: trainers = [] } = useQuery({
    queryKey: ['trainers', user?.branch?.branchId],
    queryFn: () => getPointTrainers(user.branch.branchId, user.token),
    enabled: !!user?.branch?.branchId && !!user?.token,
  });

  // 강의실 목록 조회
  // 센터설정에서 유형·기본 강의실을 바꾸면 바로 반영돼야 해서 화면 진입 시 새로 받는다.
  const { data: rooms = [] } = useQuery({
    queryKey: ['rooms', user?.branch?.branchId],
    queryFn: () => getAllPointRooms(user.branch.branchId, user.token),
    enabled: !!user?.branch?.branchId && !!user?.token,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // 그룹 수업 목록 조회
  // 필터는 받아 온 뒤 화면에서 건다 — 조회 API 는 강의실·강사를 하나씩만 받는다
  const { data: fetchedLessons = [], isLoading: isLessonsLoading } = useQuery({
    queryKey: ['groupLessons', user?.branch?.branchId, dateRange?.start, dateRange?.end],
    queryFn: () => {
      const query = {
        courseClassType: 'GROUP_LESSON',
        headBranchId: user?.headBranch?.headBranchId,
        branchId: user?.branch?.branchId,
        classDateFrom: dateRange.start,
        classDateTo: dateRange.end,
      };
      const queryString = QueryString.stringify(query);
      return getAllLessonsByType(queryString, user.token);
    },
    enabled: !!user?.branch?.branchId && !!user?.token && !!dateRange?.start && !!dateRange?.end,
  });

  const lessonsData = useMemo(
    () =>
      fetchedLessons.filter(
        lesson =>
          (selectedRoomIds.length === 0 || selectedRoomIds.includes(lesson.exerciseRoomId)) &&
          (selectedTrainerIds.length === 0 || selectedTrainerIds.includes(lesson.trainerId)),
      ),
    [fetchedLessons, selectedRoomIds, selectedTrainerIds],
  );

  // 강의실을 하나만 골랐으면 새 수업 등록 시 그 강의실을 기본으로 쓴다(예전 단일 선택과 같은 동작)
  useEffect(() => {
    const roomId = selectedRoomIds.length === 1 ? selectedRoomIds[0] : null;
    setOpens(prev => (prev.roomId === roomId ? prev : { ...prev, roomId }));
  }, [selectedRoomIds]);

  // 이벤트 데이터 변환
  const allEvents = useMemo(() => getAllLessonEvents(lessonsData), [lessonsData]);

  // 생성/수정 후 새 수업 감지 & 하이라이트
  useEffect(() => {
    if (!pendingHighlightRef.current || !lessonsData.length) return;

    const pending = pendingHighlightRef.current;
    pendingHighlightRef.current = null;

    if (pending === 'create') {
      const newIds = lessonsData
        .map(l => l.courseClassTimeId || l.courseClassId)
        .filter(id => id && !prevLessonIdsRef.current.has(id));
      if (newIds.length > 0) {
        flashHighlight(newIds.map(String));
      }
    }
  }, [lessonsData]);

  // 예약 상태별 카운트 계산 (현재 보이는 주 전체 기준)
  const reservationCounts = useMemo(() => {
    let deadlineCount = 0;
    let underCount = 0;
    let noReservationCount = 0;
    let canceledCount = 0;
    const now = moment();

    lessonsData.forEach(item => {
      // 비공개 수업도 예약 인원 기준으로 카운트
      if (item.openYn === 'N') canceledCount++;

      const totalMembers = (item.reservedMemberCount || 0) + (item.attendedMemberCount || 0);

      if (totalMembers === 0) {
        noReservationCount++;
      } else if (totalMembers >= (item.maxMemberCount || 0)) {
        deadlineCount++;
      } else {
        underCount++;
      }
    });

    return {
      deadline: deadlineCount,
      under: underCount,
      noReservation: noReservationCount,
      canceled: canceledCount,
    };
  }, [lessonsData]);

  // 선택된 수업 상세 조회
  const courseClassId = anchorEl?.eventData?.courseClassId || anchorEl?.extendedProps?.eventData?.courseClassId;

  const { data: lesson } = useQuery({
    queryKey: ['lesson', courseClassId],
    queryFn: () => getLessonById(courseClassId, user.token),
    enabled: !!courseClassId && !!user?.token,
  });

  // ============================================
  // React Query - 데이터 변경
  // ============================================

  // 수업 삭제
  const deleteWeekMutation = useMutation({
    mutationFn: cancelData => deleteWeekByIdAndType(cancelData, user.token),
    onSuccess: () => {
      setOpens({
        open: false,
        open1: false,
        open2: false,
        open3: false,
        open4: false,
        open5: false,
        openMenu: false,
        openMenu1: false,
        openCancelDialog: false,
        trainers: [],
        trainerId: null,
        roomId: opens.roomId,
        busyDates: [],
      });
      queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
      toast.success(t('수업이 취소되었습니다'));
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 일정 복사
  const copyWeeklyMutation = useMutation({
    mutationFn: async ({ selectedOpenYn, sourceFrom, sourceTo, targetFrom, targetTo }) => {
      const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

      function convertClassDateToDayOfWeek(classDate) {
        const date = new Date(classDate);
        return dayNames[date.getUTCDay()];
      }

      // 원본 기간 수업 조회
      const query = QueryString.stringify({
        courseClassType: 'GROUP_LESSON',
        headBranchId: user?.headBranch?.headBranchId,
        branchId: user?.branch?.branchId,
        classDateFrom: sourceFrom,
        classDateTo: sourceTo,
      });
      const sourceLessons = await getAllLessonsByType(query, user.token);

      if (!sourceLessons || sourceLessons.length === 0) {
        throw new Error('원본 기간에 수업이 없습니다.');
      }

      const transformedData = [];
      const uniqueCourseClassIds = [...new Set(sourceLessons.map(item => item.courseClassId))];

      const lessonDetailsMap = {};
      await Promise.all(
        uniqueCourseClassIds.map(async id => {
          try {
            const lessonDetail = await getLessonById(id, user.token);
            lessonDetailsMap[id] = lessonDetail;
          } catch (error) {
            console.error(`Failed to fetch lesson ${id}:`, error);
            lessonDetailsMap[id] = null;
          }
        }),
      );

      sourceLessons.forEach(item => {
        const dayOfWeek = convertClassDateToDayOfWeek(item.classDate);
        const groupKey = `${item.trainerId}_${item.className}_${dayOfWeek}`;
        let existingItem = transformedData.find(entry => entry._groupKey === groupKey);

        if (!existingItem) {
          const lessonDetail = lessonDetailsMap[item.courseClassId];

          existingItem = {
            _groupKey: groupKey,
            branchId: user.branch.branchId,
            courseClassType: item.courseClassType,
            className: item.className,
            exerciseRoomId: item.exerciseRoomId,
            registrationStartDate: targetFrom,
            registrationEndDate: targetTo,
            classTimeInfoList: [],
            trainerId: item.trainerId,
            tickets: item.tickets,
            trialTickets: item.trialTicketIds || [],
            templateId: lessonDetail?.templateId || null,
            membersMaxCount: item.maxMemberCount,
            membersWaitMaxCount: lessonDetail?.membersWaitMaxCount ?? item.membersWaitMaxCount ?? 0,
            memberId: item.memberId,
            memo: item.memo,
            openYn: selectedOpenYn,
            openScheduledYn: lessonDetail?.openScheduledYn || 'N',
            openScheduleType: lessonDetail?.openScheduleType || null,
            openScheduledDay: lessonDetail?.openScheduledDay || null,
            openScheduledTime: lessonDetail?.openScheduledTime || null,
            closeScheduledYn: lessonDetail?.closeScheduledYn || 'N',
            closeScheduledMinCount: lessonDetail?.closeScheduledMinCount ?? null,
            closeScheduledTime: lessonDetail?.closeScheduledTime || null,
            courseTicketDeductionCount:
              lessonDetail?.courseTicketDeductionCount ?? item.courseTicketDeductionCount ?? 1,
            allTicketsSelectedYn: lessonDetail?.allTicketsSelectedYn || 'N',
          };

          transformedData.push(existingItem);
        }

        const isDuplicate = existingItem.classTimeInfoList.some(
          t => t.dayOfWeek === dayOfWeek && t.startTime === item.startTime && t.endTime === item.endTime,
        );
        if (!isDuplicate) {
          existingItem.classTimeInfoList.push({
            dayOfWeek: dayOfWeek,
            startTime: item.startTime,
            endTime: item.endTime,
            trainerId: item.trainerId || null,
            exerciseRoomId: item.exerciseRoomId || null,
          });
        }
      });

      // 하나씩 보낸다 — 동시에 보내면 겹침 확인창이 여러 개 겹쳐 뜨고, 하나를 취소해도 나머지가 뒤늦게 저장된다.
      // 한 번 '저장'을 고르면 남은 수업도 확인 없이 force 로 보낸다.
      let forceAll = false;
      let createdCount = 0;
      try {
        for (const { _groupKey, ...res } of transformedData) {
          if (forceAll) {
            await createLesson(res, user.token, true);
          } else {
            await withOverlapConfirm(async force => {
              const result = await createLesson(res, user.token, force);
              if (force) forceAll = true;
              return result;
            });
          }
          createdCount += 1;
        }
      } catch (error) {
        // 앞서 만든 수업은 남으므로 목록을 새로 읽고 몇 개가 복사됐는지 알린다
        if (createdCount > 0) {
          queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
          if (error?.silent) toast.info(`${createdCount}개 수업만 복사되었습니다.`);
        }
        throw error;
      }
    },
    onSuccess: () => {
      toast.success('일정 복사가 완료되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
      setCopyWeeklyModalOpen(false);
      setCopyOpenYn('N');
    },
    onError: error => {
      // 겹침 확인창에서 취소한 경우 — 실패 안내 없이 끝낸다
      if (error?.silent) return;
      toast.error('대상 기간에 중복된 수업이 있어 복사에 실패했습니다. 대상 기간의 기존 수업을 확인해주세요.');
    },
  });

  // ============================================
  // 시간 배열 생성
  // ============================================
  // 행 = 표 칸 단위(30분/1시간/2시간). 범위는 기존과 같이 시작 시 ~ 종료 시 행까지(1시간 칸은 종료 시 행 포함 그대로).
  // 1시간 외 단위는 24:00 이후 행을 만들지 않고, 칸 단위로 안 나눠지면 마지막 행은 남은 길이만큼이다.
  // 행 = { label, startMinutes(자정부터 분), minutes(행 길이) }
  const { timeUnitMinutes } = groupSetting;
  const hours = useMemo(() => {
    const startMinutes = Number(periodTimes.startTime) * 60;
    const rangeEnd = (Number(periodTimes.endTime) + 1) * 60;
    const lastMinutes = timeUnitMinutes === 60 ? rangeEnd : Math.min(rangeEnd, 24 * 60);
    const rows = [];
    for (let minutes = startMinutes; minutes < lastMinutes; minutes += timeUnitMinutes) {
      rows.push({
        label: `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
        startMinutes: minutes,
        minutes: Math.min(timeUnitMinutes, lastMinutes - minutes),
      });
    }
    return rows;
  }, [periodTimes, timeUnitMinutes]);

  // lessonData에 tickets, trainers, rooms 병합
  const mergedLessonData = useMemo(
    () => ({
      ...lessonData,
      tickets,
      trainers,
      rooms,
    }),
    [lessonData, tickets, trainers, rooms],
  );

  // ============================================
  // 핸들러
  // ============================================

  // 주간 제목 생성
  const getWeekTitle = () => {
    // 한 달 안 / 달 걸침 / 연도 걸침 표기는 지점 주간 제목과 같은 규칙
    return formatWeekTitle(currentWeekStart, currentWeekStart.clone().add(6, 'days'));
  };

  // 날짜 네비게이션
  const goToPrevWeek = () => {
    setWeekAnchor(currentWeekStart.clone().subtract(7, 'days'));
  };

  const goToNextWeek = () => {
    setWeekAnchor(currentWeekStart.clone().add(7, 'days'));
  };

  const goToToday = () => {
    setWeekAnchor(moment());
  };

  // 셀 클릭 핸들러 (수업 등록). startMinutes = 클릭한 선택 단위 구간의 시작 시각(자정부터 분)
  const handleCellClick = (startMinutes, dayIndex) => {
    if (!canGroupCreate) {
      toast.error(t('수업 등록 권한이 없습니다.'));
      return;
    }
    // 24:00 행(종료 24시 경계 라벨)에서는 수업을 시작할 수 없다(지점 스케줄과 같은 규칙)
    if (startMinutes >= DAY_END_MINUTES) return;
    const toHHmm = minutes =>
      `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
    const [hourStr, minuteStr] = toHHmm(startMinutes).split(':');
    // 종료는 시작 + 50분, 시간 선택이 23:55 까지라 그 뒤로 넘기지 않는다(자정 넘어 되감기지 않게)
    const endMinutes = Math.min(startMinutes + DEFAULT_CLASS_MINUTES, LAST_SELECTABLE_MINUTES);
    const clickedDate = currentWeekStart.clone().add(dayIndex, 'days');
    const day = clickedDate.day();

    const startStr = `${clickedDate.format('YYYY-MM-DD')}T${hourStr}:${minuteStr}:00`;
    // 기존과 같이 시작 + 30분 (정시 클릭이면 HH:30), 23:55 를 넘기지 않는다
    const endStr = `${clickedDate.format('YYYY-MM-DD')}T${toHHmm(Math.min(startMinutes + 30, LAST_SELECTABLE_MINUTES))}:00`;

    setCalendarObj({
      start: clickedDate.toDate(),
      startStr: startStr,
      endStr: endStr,
      view: { calendar: { unselect: () => {}, addEvent: () => {} } },
    });

    setLessonData(prev => ({
      ...prev,
      periodStart: clickedDate.format('YYYY-MM-DD'),
      currentDayInfo: {
        timeStart: `${hourStr}:${minuteStr}:00`,
        timeEnd: `${toHHmm(endMinutes)}:00`,
        week: day,
      },
    }));
    setScheduleCreateOpen(true);
  };

  // 복사·붙여넣기(#143) — 이 화면은 그룹수업만 보이므로 그룹수업만 붙여넣는다.
  // 담당강사 컬럼이 없어 담당자는 원본 그대로이고 날짜·시간만 바뀐다.
  // 붙여넣기가 시간 충돌로 거부되면 부딪힌 수업 주로 옮겨 카드를 강조한다(수업 이동 도착 하이라이트와 같은 방식).
  // 상담과 부딪힌 경우는 이 화면에 상담 카드가 없어 토스트만 남는다.
  const [conflictFocus, setConflictFocus] = useState(null);
  const focusConflict = conflict => {
    // 그룹수업과 부딪힌 경우만 주 이동·강조한다. 개인·OT 수업은 이 화면에 카드가 없고,
    // classType 이 없는 응답(구 백엔드)도 이동하지 않는다 — 안내 토스트는 호출부가 이미 띄웠다.
    if (conflict?.type !== 'CLASS' || conflict.classType !== 'GROUP_LESSON' || !conflict.id) return;
    const weekStart = startOfWeekBy(moment(conflict.date, 'YYYY-MM-DD'), groupSetting.weekStartDay);
    if (conflict.date && !weekStart.isSame(currentWeekStart, 'day')) {
      setWeekAnchor(weekStart);
    }
    setConflictFocus({ id: String(conflict.id), expiresAt: Date.now() + 5000 });
  };

  const schedulePaste = useSchedulePaste({
    token: user?.token,
    branchId: user?.branch?.branchId,
    toast,
    onPasted: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['groupLessons'] }),
        queryClient.invalidateQueries({ queryKey: ['privateLessons', user?.branch?.branchId] }),
      ]),
    onConflict: focusConflict,
    canPaste: canGroupCreate,
    canDelete: canGroupEdit,
    allowedPasteKinds: ['GROUP_LESSON'],
  });
  const scheduleDelete = useScheduleDelete({ token: user?.token, toast });

  const handleCardContextMenu = (e, event) =>
    schedulePaste.openCardMenu(e, event?.extendedProps?.eventData);

  // startMinutes = 우클릭한 선택 단위 구간의 시작 시각. 정시는 기존처럼 startHour 만, 분이 있으면 startTime 도 넘긴다
  const handleCellContextMenu = (e, startMinutes, dayIndex) => {
    if (!canGroupCreate) return;
    schedulePaste.openEmptyMenu(e, {
      date: currentWeekStart.clone().add(dayIndex, 'days').format('YYYY-MM-DD'),
      startHour: Math.floor(startMinutes / 60),
      ...(startMinutes % 60
        ? { startTime: `${String(Math.floor(startMinutes / 60)).padStart(2, '0')}:${String(startMinutes % 60).padStart(2, '0')}` }
        : {}),
      trainerId: null,
      trainerName: '',
    });
  };

  // 이벤트 클릭 핸들러
  const handleEventClick = (e, event) => {
    const target = e.currentTarget;
    setAnchorEl(event);
    setOpens(prev => ({ ...prev, openMenu: target }));
  };

  // 강의실 선택 핸들러
  const handleTrainerCheckbox = trainerId => {
    setSelectedTrainerIds(prev =>
      prev.includes(trainerId) ? prev.filter(id => id !== trainerId) : [...prev, trainerId],
    );
  };

  // 강의실 필터 목록 — 그룹 수업에 쓰는 강의실 + 이미 골라 둔 강의실(유형이 바뀌어도 해제할 수 있게).
  // 임직원 필터 모달을 그대로 쓰려고 { trainerId, trainerName } 모양으로 맞춘다
  const roomFilterItems = useMemo(
    () =>
      rooms
        .filter(
          room =>
            selectedRoomIds.includes(room.branchExerciseRoomId) ||
            filterRoomsByLessonType([room], 'GROUP_LESSON').length > 0,
        )
        .map(room => ({ trainerId: room.branchExerciseRoomId, trainerName: room.branchExerciseRoomName })),
    [rooms, selectedRoomIds],
  );
  const handleRoomCheckbox = roomId => {
    setSelectedRoomIds(prev => (prev.includes(roomId) ? prev.filter(id => id !== roomId) : [...prev, roomId]));
  };
  const handleSelectAllRooms = () => {
    setSelectedRoomIds(prev =>
      prev.length === roomFilterItems.length ? [] : roomFilterItems.map(room => room.trainerId),
    );
  };

  const handleSelectAllTrainers = () => {
    setSelectedTrainerIds(prev => (prev.length === trainers.length ? [] : trainers.map(trainer => trainer.trainerId)));
  };

  // 메뉴 핸들러
  const handleCloseMenu = () => {
    setOpens(prev => ({ ...prev, openMenu: false }));
  };

  // 수업 취소 다이얼로그 핸들러
  const handleCancelDialogClose = () => {
    setOpens(prev => ({ ...prev, openCancelDialog: false }));
  };

  const handleCancelConfirm = () => {
    const eventData = anchorEl?.eventData || anchorEl?.extendedProps?.eventData;

    const cancelData = {
      lessonDeleteType: 'one',
      courseClassId: eventData.courseClassId,
      courseClassTimeId: eventData.courseClassTimeId,
    };

    deleteWeekMutation.mutate(cancelData);
    handleCancelDialogClose();
  };

  const handleDateSelect = () => {
    let calendarApi = calenderObj.view.calendar;
    calendarApi.unselect();
    if (lessonData.name) {
      calendarApi.addEvent({
        id: createEventId(),
        title: lessonData.name,
        start: calenderObj.startStr,
        end: calenderObj.endStr,
        allDay: calenderObj.allDay,
        backgroundColor: '#0F7F85',
      });
    }
  };

  // 주간 복사 모달 열기
  const handleCopyWeekly = () => {
    setCopySourceFrom(currentWeekStart.format('YYYY-MM-DD'));
    setCopySourceTo(currentWeekStart.clone().add(6, 'days').format('YYYY-MM-DD'));
    setCopyTargetFrom(currentWeekStart.clone().add(7, 'days').format('YYYY-MM-DD'));
    setCopyTargetTo(currentWeekStart.clone().add(13, 'days').format('YYYY-MM-DD'));
    setCopyWeeklyModalOpen(true);
  };

  // 일정 복사 확인
  const handleCopyWeeklyConfirm = () => {
    if (!copySourceFrom || !copySourceTo || !copyTargetFrom || !copyTargetTo) {
      toast.error('기간을 모두 설정해주세요');
      return;
    }
    copyWeeklyMutation.mutate({
      selectedOpenYn: copyOpenYn,
      sourceFrom: copySourceFrom,
      sourceTo: copySourceTo,
      targetFrom: copyTargetFrom,
      targetTo: copyTargetTo,
    });
  };

  // 주간 복사 모달 닫기
  const handleCopyWeeklyModalClose = () => {
    setCopyWeeklyModalOpen(false);
    setCopyOpenYn('N');
    setCopySourceFrom('');
    setCopySourceTo('');
    setCopyTargetFrom('');
    setCopyTargetTo('');
  };

  const pendingHighlightRef = useRef(null); // 'create' | courseClassTimeId | null

  // 수업 생성/수정 후 리프레시
  // mode: 'create' (새 수업 감지), courseClassTimeId (수정 하이라이트), 'skip' (하이라이트 없음)
  const handleRefreshLessons = (updatedClassTimeId) => {
    prevLessonIdsRef.current = new Set(lessonsData.map(l => l.courseClassTimeId || l.courseClassId));
    if (updatedClassTimeId === 'skip') {
      pendingHighlightRef.current = null;
    } else {
      pendingHighlightRef.current = updatedClassTimeId ? null : 'create';
    }
    setAnchorEl(null);
    queryClient.removeQueries({ queryKey: ['lesson'] });
    queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
    if (updatedClassTimeId && updatedClassTimeId !== 'skip') {
      clearTimeout(highlightDelayTimerRef.current);
      highlightDelayTimerRef.current = setTimeout(() => flashHighlight([String(updatedClassTimeId)]), 800);
    }
  };

  // 이벤트 데이터 가져오기
  const getEventData = () => {
    return anchorEl?.eventData || anchorEl?.extendedProps?.eventData;
  };

  // ============================================
  // Effects
  // ============================================
  useEffect(() => {
    toTop();
  }, []);

  // 충돌 포커스 — 부딪힌 수업 카드가 그려지면 스크롤·하이라이트. 끝내 없으면(필터·삭제) 잠시 뒤 포기한다.
  useEffect(() => {
    if (!conflictFocus) return undefined;
    let timer;
    const tryFocus = () => {
      // 만료됐으면 정리하고 끝
      if (Date.now() > conflictFocus.expiresAt) {
        setConflictFocus(null);
        return;
      }
      const target = document.querySelector(`[data-class-time-id="${conflictFocus.id}"]`);
      if (!target) {
        // 주 이동 후 데이터 로드를 기다리며 만료까지 짧게 재시도한다
        timer = setTimeout(tryFocus, 300);
        return;
      }
      scrollToCard(target);
      flashHighlight([conflictFocus.id]);
      setConflictFocus(null);
    };
    timer = setTimeout(tryFocus, 400);
    return () => clearTimeout(timer);
  }, [conflictFocus, lessonsData]);

  // 개인 스케줄 패널 "수업으로 이동" 도착 처리 — 대상 카드로 스크롤 + 하이라이트 후 쿼리 파라미터 제거 (1회만 실행)
  useEffect(() => {
    if (!focusClassTimeIdParam || focusAppliedRef.current || !lessonsData.length) return;

    const target = document.querySelector(`[data-class-time-id="${focusClassTimeIdParam}"]`);
    if (!target) return; // 다른 주로 이동했거나 삭제된 경우 조용히 무시

    focusAppliedRef.current = true;
    scrollToCard(target);
    flashHighlight([focusClassTimeIdParam]);
    setSearchParams({}, { replace: true });
  }, [focusClassTimeIdParam, lessonsData, setSearchParams]);

  return (
    <AnimationPage>
      <PageContainer elevation={0}>
        {selectedTabChild === 1 && (
          <ContentWrapper>
            <GroupLessonHeader
              onOpenSettings={() => setSettingsOpen(true)}
              goToToday={goToToday}
              goToPrevWeek={goToPrevWeek}
              goToNextWeek={goToNextWeek}
              weekTitle={getWeekTitle()}
              selectedRoomCount={selectedRoomIds.length}
              onRoomFilterClick={() => setOpenRoomFilter(true)}
              selectedTrainerCount={selectedTrainerIds.length}
              onTrainerFilterClick={() => setOpenTrainerFilter(true)}
              onCopyWeekly={handleCopyWeekly}
              isEventsEmpty={!allEvents || !allEvents.length}
              isCopyLoading={copyWeeklyMutation.isPending}
              selectedStatusFilter={selectedStatusFilter}
              onStatusFilterChange={setSelectedStatusFilter}
              reservationDeadLine={reservationCounts.deadline}
              outOfReservation={reservationCounts.under}
              noReservation={reservationCounts.noReservation}
              cellHeight={cellHeight}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onOpenTemplateManage={() => setTemplateManageOpen(true)}
              onOpenBulkEdit={() => setBulkEditOpen(true)}
              onOpenBatchReserve={() => setBatchReserveOpen(true)}
              canBatchReserve={canGroupAttendance}
              onOpenGuide={() => setGuideOpen(true)}
            />

            <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Backdrop open={isLessonsLoading} sx={{ position: 'absolute', zIndex: 10, backgroundColor: 'rgba(255,255,255,0.7)' }}>
                <CircularProgress sx={{ color: '#4AB3BC' }} />
              </Backdrop>
              <GroupLessonCalendar
                hours={hours}
                currentWeekStart={currentWeekStart}
                periodTimes={periodTimes}
                allEvents={allEvents}
                selectedStatusFilter={selectedStatusFilter}
                mainLang={mainLang}
                cellHeight={cellHeight}
                timeUnitMinutes={timeUnitMinutes}
                snapMinutes={Math.min(groupSetting.snapMinutes, timeUnitMinutes)} // 선택 단위는 표 칸보다 크지 않게
                cardMinMinutes={groupSetting.cardMinMinutes}
                onCellClick={handleCellClick}
                onCellContextMenu={handleCellContextMenu}
                onEventClick={handleEventClick}
                onEventContextMenu={handleCardContextMenu}
                isMenuOpen={Boolean(opens.openMenu)}
                highlightedIds={highlightedClassTimeIds}
                onClearHighlight={() => setHighlightedClassTimeIds([])}
              />
            </Box>
          </ContentWrapper>
        )}
      </PageContainer>

      <SchedulePasteLayer paste={schedulePaste} remove={scheduleDelete} />

      {/* 모달들 */}
      <TrainerFilterModal
        open={openTrainerFilter}
        onClose={() => setOpenTrainerFilter(false)}
        trainers={trainers}
        selectedTrainerIds={selectedTrainerIds}
        onTrainerCheckbox={handleTrainerCheckbox}
        onSelectAllTrainers={handleSelectAllTrainers}
        onReset={() => setSelectedTrainerIds([])}
        onApply={() => setOpenTrainerFilter(false)}
      />

      <TrainerFilterModal
        open={openRoomFilter}
        onClose={() => setOpenRoomFilter(false)}
        title="강의실 필터"
        unit="개"
        groupTitleOf={null}
        trainers={roomFilterItems}
        selectedTrainerIds={selectedRoomIds}
        onTrainerCheckbox={handleRoomCheckbox}
        onSelectAllTrainers={handleSelectAllRooms}
        onReset={() => setSelectedRoomIds([])}
        onApply={() => setOpenRoomFilter(false)}
      />

      <GroupScheduleSettingModal
        open={settingsOpen}
        onClose={closeGroupSetting}
        onPreviewChange={setGroupSettingPreview}
        branchId={user?.branch?.branchId}
        token={user?.token}
        sharedTimes={sharedPeriodTimes}
        setting={savedGroupSetting}
        settingLoading={groupSettingLoading}
        settingError={groupSettingError}
        rooms={rooms}
        canManageRooms={canManageRooms}
      />

      <EventMenu
        anchorEl={opens.openMenu}
        open={Boolean(opens.openMenu)}
        onClose={handleCloseMenu}
        eventData={getEventData()}
        canAttendance={canGroupAttendance}
        canEdit={canGroupEdit}
        canCancel={canGroupCancel}
        onAttendance={() => setOpens(prev => ({ ...prev, openMenu: false, open1: true }))}
        onEdit={() => setOpens(prev => ({ ...prev, openMenu: false, open4: true }))}
        onCancel={() => setOpens(prev => ({ ...prev, openMenu: false, openCancelDialog: true }))}
      />

      <CancelSuccessModal open={opens.open3} onClose={() => setOpens(prev => ({ ...prev, open3: false }))} />

      <BusyDatesModal
        open={opens?.busyDates?.length > 0}
        busyDates={opens.busyDates}
        onClose={() => setOpens(prev => ({ ...prev, busyDates: [] }))}
      />

      <CancelTypeDialog
        open={opens.openCancelDialog}
        onClose={handleCancelDialogClose}
        onConfirm={handleCancelConfirm}
        isLoading={deleteWeekMutation.isPending}
      />

      <CopyWeeklyConfirmModal
        open={copyWeeklyModalOpen}
        onClose={handleCopyWeeklyModalClose}
        onConfirm={handleCopyWeeklyConfirm}
        openYn={copyOpenYn}
        onOpenYnChange={setCopyOpenYn}
        isLoading={copyWeeklyMutation.isPending}
        sourceFrom={copySourceFrom}
        sourceTo={copySourceTo}
        targetFrom={copyTargetFrom}
        targetTo={copyTargetTo}
        onSourceFromChange={setCopySourceFrom}
        onSourceToChange={setCopySourceTo}
        onTargetFromChange={setCopyTargetFrom}
        onTargetToChange={setCopyTargetTo}
      />

      {opens.open && (
        <GroupLessonEditModal
          mode="create"
          user={user}
          type="GROUP_LESSON"
          opens={opens}
          setOpens={setOpens}
          lessonData={mergedLessonData}
          setLessonData={setLessonData}
          onSuccess={handleRefreshLessons}
          handleDateSelect={handleDateSelect}
        />
      )}

      {opens.open4 && (
        <GroupLessonEditModal
          mode="edit"
          user={user}
          type="GROUP_LESSON"
          opens={opens}
          setOpens={setOpens}
          lessonData={{ ...mergedLessonData, ...(anchorEl?.extendedProps || anchorEl) }}
          setLessonData={setLessonData}
          lesson={lesson}
          onSuccess={handleRefreshLessons}
        />
      )}

      {opens.open1 && <LessonUsers opens={opens} setOpens={setOpens} lesson={lesson} anchorEl={anchorEl} />}

      {/* 템플릿 관리 */}
      <TemplateManageModal
        open={templateManageOpen}
        onClose={() => setTemplateManageOpen(false)}
        tickets={tickets}
      />

      {/* 새 일정 등록 (3열 레이아웃) */}
      <ScheduleCreateModal
        open={scheduleCreateOpen}
        onClose={() => setScheduleCreateOpen(false)}
        user={user}
        lessonData={lessonData}
        onSuccess={handleRefreshLessons}
        tickets={tickets}
        trainers={trainers}
        rooms={rooms}
        defaultRoomId={savedGroupSetting.defaultRoomId}
      />

      {/* 스케줄 일괄수정 */}
      <GroupLessonBulkEditModal
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        trainers={trainers}
        rooms={rooms}
        tickets={tickets}
        user={user}
        onRefresh={handleRefreshLessons}
        currentWeekStart={currentWeekStart}
      />

      {/* 기존 그룹수업 × 여러 회원 일괄예약 */}
      <GroupBatchReserveModal
        open={batchReserveOpen}
        onClose={() => setBatchReserveOpen(false)}
        user={user}
        tickets={tickets}
        onRefresh={handleRefreshLessons}
      />

      {/* 가이드 모달 */}
      <GroupLessonGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </AnimationPage>
  );
};

export default GroupLesson;
